import { useState } from "react";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { ArrowRight, PhoneMissed, MessageSquare, ShieldCheck, Bot, Workflow, CalendarCheck, Tag } from "lucide-react";
import { BookingModal } from "@/components/BookingModal";
import { DiscountModal } from "@/components/DiscountModal";
const Index = () => {
  const [missedCalls, setMissedCalls] = useState(50);
  const [avgValue, setAvgValue] = useState(500);
  const [closeRate, setCloseRate] = useState(20);

  const lostRevenue = missedCalls * avgValue * (closeRate / 100);
  const rescuedRevenue = lostRevenue * 0.4; // Assuming 40% rescue rate

  return (
    <Layout>
      <SEO
        title="Automation & AI for Small Businesses in Edmonton | Missed Call Rescue"
        description="Missed Call Rescue provides top-tier automation and AI solutions for small businesses in Edmonton. Automate workflows, capture leads, and never miss a call."
        canonical="/"
        keywords="automation and ai for small businesses in edmonton, AI solutions Edmonton, small business automation Edmonton, automated text back, AI receptionist Edmonton"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Missed Call Rescue",
            url: "https://missedcallrescuecanada.com",
            telephone: ["1-855-780-3522", "780-237-6933"],
            email: "missedcallrescuecanada@gmail.com",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Edmonton",
              addressRegion: "AB",
              postalCode: "T5L2C2",
              addressCountry: "CA",
            },
            description: "Edmonton's premier provider of automation and AI solutions for small businesses. Automated text back, AI receptionist, and appointment booking.",
            areaServed: "Edmonton, Alberta, Canada",
            priceRange: "$$",
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Missed Call Text Back & AI Automation",
            provider: {
              "@type": "LocalBusiness",
              name: "Missed Call Rescue",
              url: "https://missedcallrescuecanada.com",
            },
            areaServed: "Edmonton, Alberta, Canada",
            description: "Automated text back, AI voice receptionist, automated appointment booking, and workflow automation for small businesses.",
            serviceType: "AI automation and customer service solutions for small businesses",
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What is Missed Call Rescue?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Missed Call Rescue is an Edmonton-based AI and marketing automation agency that helps small businesses capture missed calls, automate customer follow-up, and book appointments 24/7 using conversational AI and automated workflows.",
                },
              },
              {
                "@type": "Question",
                name: "How does the missed call text back feature work?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "When you miss a call, our system instantly sends an automatic text message to the caller letting them know you'll be in touch quickly, ensuring no lead goes cold and no customer turns to a competitor.",
                },
              },
              {
                "@type": "Question",
                name: "What is an AI voice receptionist?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "An AI voice receptionist answers your business calls 24/7, can answer questions, qualify leads, schedule appointments, and transfer calls — so you never miss a customer opportunity even when you're busy.",
                },
              },
              {
                "@type": "Question",
                name: "What types of businesses does Missed Call Rescue serve in Edmonton?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We serve HVAC services, plumbers, roofers, autobody shops, mechanics, realtors, lawyers, hair salons, dentists, veterinary clinics, property management companies, home care agencies, mortgage brokers, med spas, tree service companies, and many other service-based businesses in Edmonton and across Alberta.",
                },
              },
              {
                "@type": "Question",
                name: "Does Missed Call Rescue offer automated appointment booking?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. Customers can book appointments online anytime, and our AI receptionist can book, reschedule, cancel, manage waitlists, and confirm appointments across phone calls, SMS, and website chat with automated reminders that reduce no-shows.",
                },
              },
              {
                "@type": "Question",
                name: "How much does Missed Call Rescue cost?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We offer monthly and annual subscription plans. New customers who commit to a 1-year plan receive 30% off. Contact us at 1-855-780-3522 or 780-237-6933 for a free consultation.",
                },
              },
              {
                "@type": "Question",
                name: "Does Missed Call Rescue serve businesses outside Edmonton?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. While we are based in Edmonton, Alberta, we offer solutions across Canada. We have a special promotion for Alberta cities including Fort Saskatchewan, St. Albert, Sherwood Park, Leduc, Spruce Grove, Stony Plain, Nisku, and Acheson.",
                },
              },
            ],
          },
        ]}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-2xl">
<h1 
                className="mb-6 text-4xl font-extrabold tracking-tight text-primary sm:text-5xl md:text-6xl text-center leading-tight"
              >
                Every Missed Call<br />
                <span className="text-4xl sm:text-5xl md:text-6xl font-bold text-primary">is a</span><br />
                Missed Opportunity
              </h1>
              <p 
                className="mb-8 text-xl text-muted-foreground max-w-2xl font-light"
              >
                In today's fast-paced world, customers expect immediate responses. If you don't answer right away, they will call your competitor! We make sure that never happens with our intelligent AI solutions.
              </p>
              <div 
                className="flex flex-col sm:flex-row gap-4"
              >
                <BookingModal>
                  <Button size="lg" className="text-lg px-8 cursor-pointer">
                    Get Started Today
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </BookingModal>
                <DiscountModal>
                  <Button size="lg" variant="outline" className="text-lg px-8 border-primary text-primary hover:bg-primary/5 cursor-pointer">
                    <Tag className="mr-2 h-5 w-5" />
                    Claim 30% Off Annual
                  </Button>
                </DiscountModal>
              </div>
            </div>
            <div 
              className="relative hidden lg:block"
            >
              <picture>
                <source srcSet="https://wsrv.nl/?url=https%3A%2F%2Fvibe.filesafe.space%2F1776204073728798439%2Fassets%2F4ab76731-c475-4df2-aa7e-29cc501c69b3.png&output=webp" type="image/webp" />
                <img 
                  src="https://vibe.filesafe.space/1776204073728798439/assets/4ab76731-c475-4df2-aa7e-29cc501c69b3.png" 
alt="AI Services Edmonton — Missed Call Rescue automated text back and AI receptionist help roofers, plumbers, and Edmonton small businesses capture missed calls and book appointments automatically" 
                  className="relative w-full h-[500px] object-cover rounded-3xl shadow-xl border border-border/50"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  width={800}
                  height={500}
                />
              </picture>
            </div>
          </div>
        </div>
      </section>



      {/* Core Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Powerful Automation Features</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to capture leads, engage customers, and book appointments on autopilot.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 transition-all hover:shadow-md hover:border-primary/20">
              <div className="h-12 w-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-6">
                <MessageSquare className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Auto Text Back</h3>
              <p className="text-slate-600">
                Instantly reply to missed calls with a customized text message, ensuring no lead goes cold.
              </p>
            </div>

            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 transition-all hover:shadow-md hover:border-secondary/20">
              <div className="h-12 w-12 bg-secondary/10 text-secondary rounded-xl flex items-center justify-center mb-6">
                <Bot className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Conversational AI</h3>
              <p className="text-slate-600">
                Engage customers with intelligent, human-like AI that answers questions and handles inquiries 24/7.
              </p>
            </div>

            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 transition-all hover:shadow-md hover:border-accent/20">
              <div className="h-12 w-12 bg-accent/10 text-accent rounded-xl flex items-center justify-center mb-6">
                <Workflow className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Automated Workflows</h3>
              <p className="text-slate-600">
                Streamline your operations with custom triggers and actions that nurture leads automatically.
              </p>
            </div>

            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 transition-all hover:shadow-md hover:border-primary/20">
              <div className="h-12 w-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-6">
                <CalendarCheck className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Automated Bookings</h3>
              <p className="text-slate-600">
                Let your AI assistant schedule appointments directly into your calendar without lifting a finger.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Calculate Your ROI</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              See exactly how much revenue you're leaving on the table, and how much Missed Call Rescue can help you recover.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="border-slate-200 shadow-lg overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="p-8 bg-white">
                  <div className="space-y-8">
                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="font-semibold text-slate-700">Missed Calls (per month)</label>
                        <span className="font-bold text-primary">{missedCalls}</span>
                      </div>
                      <Slider
                        value={[missedCalls]}
                        onValueChange={(val) => setMissedCalls(val[0])}
                        max={500}
                        step={10}
                        aria-label="Missed Calls (per month)"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="font-semibold text-slate-700">Average Customer Value</label>
                        <span className="font-bold text-primary">${avgValue}</span>
                      </div>
                      <Slider
                        value={[avgValue]}
                        onValueChange={(val) => setAvgValue(val[0])}
                        max={5000}
                        step={50}
                        aria-label="Average Customer Value"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="font-semibold text-slate-700">Your Close Rate</label>
                        <span className="font-bold text-primary">{closeRate}%</span>
                      </div>
                      <Slider
                        value={[closeRate]}
                        onValueChange={(val) => setCloseRate(val[0])}
                        max={100}
                        step={5}
                        aria-label="Your Close Rate"
                      />
                    </div>
                  </div>
                </div>

                <div className="p-8 bg-primary text-primary-foreground flex flex-col justify-center">
                  <div className="space-y-6">
                    <div>
                      <p className="text-primary-foreground/80 font-medium mb-1">Revenue at Risk (Monthly)</p>
                      <p className="text-4xl font-bold">${lostRevenue.toLocaleString()}</p>
                    </div>
                    <div className="h-px bg-primary-foreground/20 w-full"></div>
                    <div>
                      <p className="text-primary-foreground/80 font-medium mb-1">Potential Rescued Revenue</p>
                      <p className="text-5xl font-extrabold text-emerald-400">${rescuedRevenue.toLocaleString()}</p>
                      <p className="text-sm text-primary-foreground/70 mt-2">*Based on an average 40% automated recovery rate</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>



      {/* Full Description Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
AI Solutions Edmonton Automated Text Back & AI Receptionist for Small Businesses
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed">
            <strong className="font-semibold text-primary">Missed Call Rescue</strong> your small business marketing agency and immediate customer service support utilizing Ai Receptionist! Our most popular automation services include <strong className="font-semibold text-slate-800">Automatic text back feature, Automatic Appointment Bookings</strong> and <strong className="font-semibold text-slate-800">AI Solutions in Edmonton</strong> tailored for <strong className="font-semibold text-slate-800">small business customer care</strong>. Turn every missed call into a captured lead. From HVAC Services, Plumbers, Roofers, Autobody shops, Mechanics, Realtors, Lawyers and Hair Salons, we get it you can't answer every call, don't worry, we have you covered from instant automatic missed call text back to instant appointment bookings, conversational voice ai, chat widgets, customer relationship management and online reputation management, every business needs an Ai Receptionist!
          </p>
        </div>
      </section>


      <section className="py-20 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-secondary rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-accent rounded-full opacity-20 blur-3xl"></div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Ready to rescue your missed calls?</h2>
          <p className="text-xl text-primary-foreground/80 mb-10 max-w-2xl mx-auto">
            Join hundreds of Edmonton businesses that have stopped losing clients to unanswered calls. Discover the ultimate automation and AI solutions for small businesses in Edmonton.
          </p>
          <BookingModal>
            <Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 text-lg px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-all cursor-pointer">
              Get Your Custom Solution
            </Button>
          </BookingModal>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
